import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import Nav from '@/components/Nav';
import Footer from '@/components/Footer';
import HeroSection from '@/components/sections/HeroSection';
import ExperiencesSection from '@/components/sections/ExperiencesSection';
import SpeakersSection from '@/components/sections/SpeakersSection';
import RegistrationSection from '@/components/sections/RegistrationSection';
import FastTrackSection from '@/components/sections/FastTrackSection';
import PartnersGallerySection from '@/components/sections/PartnersGallerySection';
import FAQSection from '@/components/sections/FAQSection';
import NewsSection from '@/components/sections/NewsSection';
import UpcomingEventsSection from '@/components/sections/UpcomingEventsSection';
import SectionDivider from '@/components/SectionDivider';
import StickyBottomMarquee, { STICKY_MARQUEE_SPACER_CLASS } from '@/components/StickyBottomMarquee';
import { MotionProvider } from '@/lib/motion';
import MaintenancePage from '@/pages/MaintenancePage';

const isVisible = (meta, key) => {
  const v = meta && meta[key] && meta[key].visible;
  return v !== false;
};

export default function Home() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get('/public/home').then((r) => setData(r.data)).catch((e) => setErr(e.message));
  }, []);

  // Hash landing (e.g. /#faq from the footer, /#speakers from a subpage's
  // nav): the browser's native anchor jump fires before our async content
  // exists, so it lands at the top. Once data has rendered, scroll to the
  // requested section — but only after the async News section has SETTLED
  // (it signals via window.__seiconNewsSettled / 'seicon:news-settled'),
  // so a late-arriving news grid or video can no longer insert above #faq
  // and shove it after we've scrolled. The 5s timer is purely a hang
  // guard for a stalled network, not a timing mechanism. The
  // `section[id] { scroll-margin-top: 72px }` rule in index.css keeps the
  // heading clear of the sticky nav.
  useEffect(() => {
    if (!data) return undefined;
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return undefined;
    const target = hash.slice(1);
    const scrollNow = () => {
      requestAnimationFrame(() => {
        const el = document.getElementById(target);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      });
    };
    // Only targets AT or BELOW the async News section can be displaced by
    // its late insertion. Everything above it scrolls immediately.
    const needsSettlement = target === 'faq' || target === 'news';
    if (!needsSettlement) {
      scrollNow();
      return undefined;
    }
    let finished = false;
    let fallback = null;
    const finalGo = () => {
      if (finished) return;
      finished = true;
      window.removeEventListener('seicon:news-settled', finalGo);
      if (fallback) clearTimeout(fallback);
      scrollNow();
    };
    if (window.__seiconNewsSettled) {
      finalGo();
    } else {
      window.addEventListener('seicon:news-settled', finalGo);
      // Hang guard: after 5s scroll PROVISIONALLY so a stalled network
      // never strands the visitor at the top — but keep the listener
      // armed, so if News settles later the settlement event performs
      // one final corrective scroll and only then cleans up. FAQ can
      // never be left displaced.
      fallback = setTimeout(scrollNow, 5000);
    }
    return () => {
      window.removeEventListener('seicon:news-settled', finalGo);
      if (fallback) clearTimeout(fallback);
    };
  }, [data]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-canvas text-ink-primary">
        <div className="text-center">
          <p className="font-display text-2xl uppercase">Unable to load content</p>
          <p className="font-body text-sm text-ink-muted mt-2">{err}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-canvas">
        <div className="h-8 w-8 rounded-full border-2 border-brand-orange border-t-transparent animate-spin" />
      </div>
    );
  }

  const { settings, experiences, speakers, featured_session, partners, gallery, upcoming_events } = data;
  const motion = settings.motion || {};
  const sections = settings.sections || {};
  const meta = settings.sections_meta || {};
  const nav = settings.nav || {};
  const maintenance = settings.maintenance || {};
  const sponsorshipUrl = (settings.global_ctas || {}).sponsorship_url || '';
  const sponsorshipLabel = (settings.global_ctas || {}).sponsorship_label || '';
  const presentingSponsor = settings.presenting_sponsor || null;

  if (maintenance.enabled) {
    return <MaintenancePage maintenance={maintenance} />;
  }

  const showHero = isVisible(meta, 'hero');
  // Upcoming Events has NO section-wide switch: each event carries its
  // own visible flag, and the section (heading, dividers, everything)
  // disappears entirely when zero events are visible — the markup then
  // matches the pre-feature homepage exactly.
  const eventsList = Array.isArray(upcoming_events) ? upcoming_events : [];
  const showEvents = eventsList.length > 0;
  const showExp = isVisible(meta, 'experiences');
  const showSpeakers = isVisible(meta, 'speakers');
  const showReg = isVisible(meta, 'registration');
  const showSponsors = isVisible(meta, 'sponsors');
  const showFastTrack = ((settings.fast_track || {}).visible) !== false;

  return (
    <MotionProvider motion={motion}>
      <div className={`bg-bg-canvas min-h-screen ${STICKY_MARQUEE_SPACER_CLASS}`}>
        <Nav nav={nav} motion={motion} />
        <main>
          {showHero && <HeroSection hero={settings.hero || {}} meta={meta.hero || {}} motion={motion} sponsorshipUrl={sponsorshipUrl} sponsorshipLabel={sponsorshipLabel} presentingSponsor={presentingSponsor} />}
          {/* Divider chain around Upcoming Events: with zero visible
              events these three lines collapse to exactly the old
              `showHero && showExp` single divider — never an orphan or
              a double. With events: Hero → divider → Events → divider
              → Experiences (each divider only when both sides exist). */}
          {showHero && showEvents && <SectionDivider />}
          {showEvents && <UpcomingEventsSection events={eventsList} />}
          {(showEvents || showHero) && showExp && <SectionDivider />}
          {showExp && <ExperiencesSection experiences={experiences} section={sections.experiences} meta={meta.experiences || {}} motion={motion} />}
          {showExp && showSpeakers && <SectionDivider />}
          {showSpeakers && (
            <SpeakersSection
              speakers={speakers}
              /* Hidden per Shawn 2026-05-26 — restore by changing null back to featuredSession */
              featured={null}
              section={sections.speakers}
              meta={meta.speakers || {}}
              motion={motion}
            />
          )}
          {showSpeakers && showSponsors && <SectionDivider />}
          {showSponsors && <PartnersGallerySection partners={partners} gallery={gallery} section={sections.partners} meta={meta.sponsors || {}} motion={motion} sponsorshipUrl={sponsorshipUrl} presentingSponsor={presentingSponsor} />}
          {showSponsors && showReg && <SectionDivider />}
          {showReg && <RegistrationSection registration={settings.registration || {}} section={sections.registration} meta={meta.registration || {}} motion={motion} />}
          {showReg && showFastTrack && <SectionDivider />}
          {showFastTrack && <FastTrackSection fastTrack={settings.fast_track} />}
          {showFastTrack && <SectionDivider />}
          {/* News widget — renders nothing until the first visible article
              exists, so the homepage is unchanged until Sol adds one. */}
          <NewsSection />
          <FAQSection />
        </main>
        <Footer footer={settings.footer || {}} motion={motion} presentingSponsor={presentingSponsor} />
        <StickyBottomMarquee bottomMarquee={settings.bottom_marquee} />
      </div>
    </MotionProvider>
  );
}
